// Shared domain types for the AI Study Material Generator.
// Used by both the Worker API and the React frontend.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type OutputLanguage = "hinglish" | "english" | "hindi";
export const OUTPUT_LANGUAGES: readonly OutputLanguage[] = [
  "hinglish",
  "english",
  "hindi",
] as const;

/** Selectable document templates. The default is `study-material`. */
export type DocumentType = "study-material" | "report" | "guide" | "course";
export const DOCUMENT_TYPES: readonly DocumentType[] = [
  "study-material",
  "report",
  "guide",
  "course",
] as const;

export type ChartKind = "bar" | "pie" | "waterfall" | "timeline";
export const CHART_KINDS: readonly ChartKind[] = [
  "bar",
  "pie",
  "waterfall",
  "timeline",
] as const;

export type PageKind = "cover" | "toc" | "unit" | "bonus";

export type AssetType = "image" | "pdf";

// ---------------------------------------------------------------------------
// Roadmap
// ---------------------------------------------------------------------------

export interface RoadmapSubsection {
  title: string;
  description: string;
}

export interface PlannedChart {
  kind: ChartKind;
  title: string;
  description: string;
}

export interface PlannedTable {
  title: string;
  columns: string[];
}

export interface RoadmapUnit {
  id: string;
  ord: number;
  title: string;
  subtitle?: string;
  subsections: RoadmapSubsection[];
  plannedCharts: PlannedChart[];
  plannedTables: PlannedTable[];
}

export interface Roadmap {
  units: RoadmapUnit[];
}

// ---------------------------------------------------------------------------
// Document / pages / charts / assets
// ---------------------------------------------------------------------------

export interface ChartData {
  /** Category/series labels. */
  labels: string[];
  /** One or more numeric series. */
  series: { name?: string; values: number[] }[];
}

export interface Chart {
  id: string;
  projectId: string;
  pageId: string;
  kind: ChartKind;
  title?: string;
  data: ChartData;
  /** False when the LLM returned invalid/missing data — renders a placeholder. */
  isValid: boolean;
}

export interface AssetRef {
  id: string;
  projectId: string;
  pageId?: string;
  assetType: AssetType;
  /** R2 object key (used only when R2 is enabled). */
  r2Key: string;
  /** Inline SVG markup (legacy free-tier path). */
  svg?: string;
  /** True when an image has been generated and is available at the asset URL. */
  hasImage?: boolean;
  /** Prompt/description used to (re)generate an image. */
  description?: string;
  createdAt: string;
}

export interface Page {
  id: string;
  projectId: string;
  ord: number;
  unitId?: string;
  pageKind: PageKind;
  /** Editable structured content (HTML). */
  contentHtml: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

export interface Project {
  id: string;
  topic: string;
  documentType: DocumentType;
  language: OutputLanguage;
  /** Optional per-project LLM model id; falls back to the server default. */
  llmModel?: string;
  roadmapConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Full project payload returned by GET /api/projects/:id. */
export interface ProjectDetail extends Project {
  roadmap: Roadmap;
  pages: Page[];
  charts: Chart[];
  assets: AssetRef[];
}

/** Summary row for the history list. */
export interface ProjectSummary {
  id: string;
  topic: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// API request/response contracts
// ---------------------------------------------------------------------------

export interface CreateProjectRequest {
  topic: string;
  documentType?: DocumentType;
  language?: OutputLanguage;
  llmModel?: string;
}

export interface UpdateSettingsRequest {
  documentType?: DocumentType;
  language?: OutputLanguage;
  llmModel?: string;
}

/** A selectable model returned by GET /api/models. */
export interface ModelInfo {
  id: string;
}

export interface RoadmapRefineRequest {
  /** Natural-language suggestion (AI refine). Omit when sending a direct edit. */
  suggestion?: string;
  /** Direct edit of the full roadmap (client-side edits). */
  roadmap?: Roadmap;
}

export interface PageAiEditRequest {
  instruction: string;
}

export interface InlineEditRequest {
  contentHtml: string;
}

export interface ChartRegenerateRequest {
  instruction?: string;
}

export interface ImageGenerateRequest {
  description: string;
}

export interface ExportResponse {
  downloadUrl: string;
  exportId: string;
}

// ---------------------------------------------------------------------------
// Error envelope
// ---------------------------------------------------------------------------

export type ErrorOperation =
  | "roadmap_generate"
  | "roadmap_refine"
  | "document_generate"
  | "page_ai_edit"
  | "chart_regenerate"
  | "image_generate"
  | "export"
  | "persist";

export type ErrorReason =
  | "nvidia_error"
  | "timeout"
  | "rate_limited"
  | "invalid_data"
  | "persistence_failed"
  | "asset_missing"
  | "validation";

export interface ApiError {
  error: {
    operation: ErrorOperation;
    reason: ErrorReason;
    message?: string;
    unitId?: string;
    retryable: boolean;
  };
}
